import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import PouchDB from 'pouchdb';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { environment } from 'src/environments/environment';

export interface TripsDialogData {
  dbName: string;
}

interface TripDoc {
  _rev: string;
  _id: string;
  trip: any;
  reports?: ReportItem[];

}

interface ReportItem {
  reportType: string;
  date: string;
  confirmationNumber: string;
  reportID: string;
}


@Component({
    selector: 'app-trips-dialog',
    templateUrl: './trips-dialog.component.html',
    styleUrls: ['./trips-dialog.component.css'],
    standalone: false
})
export class TripsDialogComponent implements OnInit {
  public trips: TripDoc[] = [];
  public loadingTrips: boolean = true;
  public savingConfirmation: boolean = false;
  private db!: PouchDB.Database<TripDoc>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TripsDialogData,
    public dialogRef: MatDialogRef<TripsDialogComponent>,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {

    const token = UserStorageService.adminUser!.apiKey;
    const url = `https://${token}@${environment.databaseName}/${this.data.dbName}`;
    this.db = new PouchDB<TripDoc>(url);

this.db.allDocs<TripDoc>({ include_docs: true, startkey: 'trip-', endkey: 'trip-\ufff0' })
  .then(res => {
    this.trips = res.rows
      .map(r => {
        const doc = r.doc!;
        // a one-time calculation of the flattened+filtered reports
        doc.reports = this.getReports(doc.trip);
        return doc;
      })
      // now you can still sort by start-date if you like
      .sort((a, b) =>
        Date.parse(b.trip.startFishingReport.startDate) -
        Date.parse(a.trip.startFishingReport.startDate)
      );
    this.loadingTrips = false;
    // if you ever need to force Angular to notice:
    // this.cd.detectChanges();
  });
  }

    /** map of reportType → the date-field on that report object */
  private readonly dateFieldMap: Record<string,string> = {
    startFishingReport:    'startDate',
    dailyCatchReports:     'dateFished',
    pauseFishingReports:   'pauseDate',
    endFishingReport:      'endDate',
    offloadReports:        'offloadDate',
  };


  /** Collect all the populated report types in one array */
  getReports(trip: any) {
    const keys = [
      'startFishingReport',
      'endFishingReport',
      'pauseFishingReports',
      'dailyCatchReports',
      'offloadReports',
    ];

    // 1) Flatten only submitted reports
    const allReports = keys.reduce((acc: any[], key: string) => {
      const value = trip[key];
      if (!value) return acc;

      // helper to push a single report if submitted
      const pushIfSubmitted = (r: any) => {
        if (r.isSubmitted) {
          acc.push({ type: key, ...r });
        }
      };

      if (Array.isArray(value)) {
        value.forEach(pushIfSubmitted);
      } else {
        pushIfSubmitted(value);
      }

      return acc;
    }, []);

    // 2) Map into the fields we want
    return allReports.map((r: any) => {
      const field = this.dateFieldMap[r.type];
      const date = (field && r[field]);
      return {
        reportType: r.type,
        date,
        confirmationNumber: r.confirmationNumber,
        reportID: r.reportID
      };
    });
  }

  saveTrip(doc: TripDoc) {
    this.savingConfirmation = true;
    
    //Need to fetch the latest version of the document to get the latest _rev number
    //Performing multiple connfirmation number updates can cause conflicts if the _rev is not current
    this.db.get(doc._id).then(existingDoc => {
      // 1) for each edited report in doc.reports, write its confirmationNumber
      doc.reports!.forEach(rpt => {
        const key = rpt.reportType;
        const bucket = (doc.trip as any)[key];

        if (Array.isArray(bucket)) {
          // find the matching entry by reportID and patch it
          const orig = bucket.find((item: any) => item.reportID === rpt.reportID);
          if (orig) {
            orig.confirmationNumber = rpt.confirmationNumber;
          }
        }
        else if (bucket && bucket.reportID === rpt.reportID) {
          // single-object report
          bucket.confirmationNumber = rpt.confirmationNumber;
        }
      });

      existingDoc.trip = doc.trip; // 1) update the _rev to the latest version
      // 2) now save  
      this.db.put(existingDoc)
        .then(() => {
          console.log(`Trip ${doc._id} saved`);
          this.toastr.success("Save Success")
          this.savingConfirmation = false;
        })
        .catch(err => {
          console.error(`Failed saving ${doc._id}`, err);
          this.savingConfirmation = false;
        });
      });
    

  }

  trackByDocId(_i: number, doc: TripDoc) {
    return doc._id;
  }

  trackByReportId(_i: number, rpt: any) {
    return rpt.reportID;
  }
}
